// src/components/dev/ImpersonationBanner.jsx
//
// Fixed yellow banner at the top of the viewport, visible only when
// usePlatformOrgContext.activeOrgId is set (i.e., when a platform admin
// has switched orgs via ?as_org=...). Renders nothing when not impersonating.
//
// No dismiss button — clearing the banner requires clearing the
// impersonation itself (via ?as_org=clear or the "Clear" link in the
// banner). Dismissing without clearing would be a footgun: the operator
// would think they're back to their real org but every API call would
// still inject X-Active-Org.
//
// ── IN NORMAL FLOW, NOT FIXED. 2026-08-31. ─────────────────────────────────
//
// This was `fixed top-0 left-0 right-0 z-[9999]`, which took it out of flow
// and laid it OVER the header rather than displacing it. Anything in the top
// strip underneath was unclickable for as long as the banner showed — a bug in
// its own right, and one the licence banner would have inherited by copying
// the pattern.
//
// It is now the first child of the layout, above the header, in flow. Flow
// does the displacement; a second banner stacks beside this one with no
// arithmetic; and there is no height constant anywhere to go stale when
// someone edits this text.
//
// NOT a spacer and NOT conditional top padding on the header. Both require the
// height to be kept in sync by hand, and with two banners that can co-occur
// that becomes conditional arithmetic — wrong the first time the text wraps to
// two lines.
//
// Nothing is lost by leaving `fixed`. The shells that mount this are
// `h-screen flex flex-col` and never scroll as a page — inner regions scroll
// inside `flex-1 overflow-hidden`. So a flow child at the top is permanently
// visible, which is the property `fixed` was there to guarantee.

import { AlertTriangle } from "lucide-react";
import {
  usePlatformOrgContext,
  writeImpersonation,
} from "../../stores/usePlatformOrgContext";

export default function ImpersonationBanner() {
  const activeOrgId   = usePlatformOrgContext((s) => s.activeOrgId);
  const activeOrgName = usePlatformOrgContext((s) => s.activeOrgName);
  const activeOrg     = usePlatformOrgContext((s) => s.activeOrg);
  const clearActiveOrg = usePlatformOrgContext((s) => s.clearActiveOrg);

  if (!activeOrgId) return null;

  // ── THE SERVER'S ANSWER OVERRIDES THE CLIENT'S BELIEF, BUT ONLY TO HIDE ─────
  //
  // This banner used to be driven entirely by client state: a store the client
  // wrote, from a key the client stored. Nothing confirmed the server had
  // honoured X-Active-Org. A caller whose header was ignored — a non-admin, or
  // an admin naming their own org — saw a banner asserting an acting org the
  // response never used. /api/v1/orgs/active now returns is_impersonating and
  // usePlatformOrgContext.activeOrg carries it.
  //
  // FAILS VISIBLE, NOT HIDDEN. Only an EXPLICIT false hides the banner. While
  // the /orgs/active fetch is in flight activeOrg is null, and if it fails it
  // stays null — in both cases the banner shows. The failure mode being guarded
  // against is an admin who does not realise they are impersonating, so an
  // uncertain banner must appear rather than disappear.
  if (activeOrg && activeOrg.is_impersonating === false) return null;

  function handleClear() {
    // The key is spelled once, in the store. This used to repeat the literal.
    writeImpersonation(null);
    clearActiveOrg();
  }

  // Short UUID alongside the friendly name so platform admins can spot
  // which UUID is in play even if the friendly name lookup is stale.
  const shortId = activeOrgId.slice(0, 8);

  return (
    <div
      className="shrink-0
                 bg-amber-500/25 border-b border-amber-500/50
                 text-amber-50 px-3 py-1
                 flex items-center justify-center gap-2
                 text-[11px] font-medium"
      role="status"
      aria-live="polite"
    >
      <AlertTriangle size={12} className="shrink-0" />
      <span>
        Impersonating
        {activeOrgName ? <> <strong>{activeOrgName}</strong> </> : " "}
        <span className="font-mono opacity-70">({shortId})</span>
      </span>
      <span className="opacity-50">·</span>
      <button
        type="button"
        onClick={handleClear}
        className="underline hover:opacity-80 transition"
        title="Stop impersonating — returns to your real org"
      >
        Clear
      </button>
    </div>
  );
}
